import { useState, useEffect, useCallback } from 'react';
import { useWebSocket } from '../contexts/WebSocketContext';

export interface SessionUpdate {
  id: string;
  userId: string;
  machineId: string;
  duration: number;
  cost: number;
  status: 'pending' | 'active' | 'completed' | 'failed';
  startTime?: string;
  endTime?: string;
  paymentMethod: 'balance' | 'pix';
}

export interface SessionNotification {
  type: 'session_started' | 'session_ending' | 'session_completed' | 'session_failed';
  message: string;
  sessionId: string;
  timestamp: string;
}

export const useRealtimeSession = (sessionId?: string) => {
  const { socket, isConnected, joinSessionMonitoring } = useWebSocket();
  const [sessionData, setSessionData] = useState<SessionUpdate | null>(null);
  const [notifications, setNotifications] = useState<SessionNotification[]>([]);

  // Join session monitoring room when connected and sessionId is provided
  useEffect(() => {
    if (isConnected && sessionId) {
      joinSessionMonitoring(sessionId);
    }
  }, [isConnected, sessionId, joinSessionMonitoring]);

  // Set up event listeners
  useEffect(() => {
    if (!socket || !sessionId) return;

    // Session updates
    const handleSessionUpdate = (data: { data: SessionUpdate; timestamp: string }) => {
      if (data.data.id === sessionId) {
        setSessionData(data.data);
      }
    };

    // Session-specific notifications
    const handleSessionNotification = (data: { data: SessionNotification; timestamp: string }) => {
      if (data.data.sessionId === sessionId) {
        setNotifications(prev => {
          const updated = [data.data, ...prev];
          return updated.slice(0, 10); // Keep last 10 notifications
        });
      }
    };

    // Register event listeners
    socket.on('session-update', handleSessionUpdate);
    socket.on('session-notification', handleSessionNotification);

    // Cleanup
    return () => {
      socket.off('session-update', handleSessionUpdate);
      socket.off('session-notification', handleSessionNotification);
    };
  }, [socket, sessionId]);

  // Clear notifications
  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Get session progress percentage
  const getProgress = useCallback(() => {
    if (!sessionData || !sessionData.startTime || sessionData.status !== 'active') {
      return 0;
    }

    const startTime = new Date(sessionData.startTime);
    const totalDuration = sessionData.duration * 60; // Convert to seconds
    const elapsed = Math.floor((Date.now() - startTime.getTime()) / 1000);
    
    return Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
  }, [sessionData]);

  return {
    sessionData,
    notifications,
    progress: getProgress(),
    isConnected,
    clearNotifications
  };
};